// frontend/src/components/Chatbot/PendingActions.tsx
// Displays actions that require user confirmation before execution.

import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { getActionIcon } from './chatUtils';

export interface ActionLike {
  id: string;
  tool?: string;
  description?: string;
  params?: Record<string, unknown>;
  [key: string]: unknown;
}

export type ExecutedAction = ActionLike & { success?: boolean; message?: string; error?: string };
export type BlockedAction = ActionLike & { reason?: string };

interface ColorScheme {
  bg: string;
  border: string;
  icon: string;
  text: string;
}

const getActionColors = (toolName: string | undefined): ColorScheme => {
  if (
    toolName?.includes('remove') ||
    toolName?.includes('delete') ||
    toolName?.includes('clear')
  ) {
    return {
      bg: 'bg-status-critical/8',
      border: 'border-status-critical/30',
      icon: 'text-status-critical',
      text: 'text-ink',
    };
  }
  if (toolName?.includes('add') || toolName?.includes('create')) {
    return {
      bg: 'bg-success/10',
      border: 'border-success/30',
      icon: 'text-success',
      text: 'text-ink',
    };
  }
  if (toolName?.includes('move')) {
    return {
      bg: 'bg-status-progress/10',
      border: 'border-status-progress/30',
      icon: 'text-status-progress',
      text: 'text-ink',
    };
  }
  return {
    bg: 'bg-status-warning/10',
    border: 'border-status-warning/30',
    icon: 'text-status-warning',
    text: 'text-ink',
  };
};

interface PendingActionItemProps {
  action: ActionLike;
  isSelected: boolean;
  onToggle: (actionId: string) => void;
  disabled: boolean;
}

const PendingActionItem = ({ action, isSelected, onToggle, disabled }: PendingActionItemProps) => {
  const Icon = getActionIcon(action.tool, AlertTriangle);
  const colors = getActionColors(action.tool);

  return (
    <div
      className={`
        flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer
        ${
          isSelected
            ? `${colors.bg} ${colors.border} ring-2 ring-offset-1 ring-primary/50`
            : 'bg-surface-card border-line-soft hover:bg-surface-sunken'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      onClick={() => !disabled && onToggle(action.id)}
    >
      <div className="flex-shrink-0 mt-0.5">
        <div
          className={`
          w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
          ${isSelected ? 'bg-primary border-primary' : 'border-line-soft bg-surface-card'}
        `}
        >
          {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
        </div>
      </div>

      <div className={`flex-shrink-0 ${colors.icon}`}>
        <Icon className="w-5 h-5" />
      </div>

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${colors.text}`}>
          {action.description || action.tool}
        </p>
        {action.params && Object.keys(action.params).length > 0 && (
          <p className="text-xs text-ink-2 mt-1 truncate">
            {Object.entries(action.params)
              .map(([k, v]) => `${k}: ${String(v)}`)
              .join(' • ')}
          </p>
        )}
      </div>
    </div>
  );
};

export interface PendingActionsProps {
  pendingActions?: ActionLike[];
  onConfirm: (approved: ActionLike[], rejectedIds: string[]) => Promise<void> | void;
  onReject: (rejectedIds?: string[]) => Promise<void> | void;
  isProcessing?: boolean;
}

const PendingActions = ({
  pendingActions = [],
  onConfirm,
  onReject,
  isProcessing = false,
}: PendingActionsProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setSelectedIds(new Set(pendingActions.map((a) => a.id)));
  }, [pendingActions]);

  const toggleAction = (actionId: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) {
        next.delete(actionId);
      } else {
        next.add(actionId);
      }
      return next;
    });
  };

  const toggleAll = (): void => {
    if (selectedIds.size === pendingActions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingActions.map((a) => a.id)));
    }
  };

  const handleConfirm = (): void => {
    const approved = pendingActions.filter((a) => selectedIds.has(a.id));
    const rejected = pendingActions.filter((a) => !selectedIds.has(a.id)).map((a) => a.id);
    onConfirm(approved, rejected);
  };

  const handleRejectAll = (): void => {
    onReject(pendingActions.map((a) => a.id));
  };

  if (pendingActions.length === 0) return null;

  const allSelected = selectedIds.size === pendingActions.length;
  const someSelected = selectedIds.size > 0;

  return (
    <div className="bg-status-warning/8 border border-status-warning/25 rounded-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-status-warning" />
        <h4 className="font-medium text-status-warning-fg">Confirm Actions</h4>
        <span className="text-sm text-amber-600">
          ({pendingActions.length} action{pendingActions.length !== 1 ? 's' : ''})
        </span>
      </div>

      <p className="text-sm text-amber-700">
        The AI assistant wants to make changes to your plan. Select which actions to approve:
      </p>

      {pendingActions.length > 1 && (
        <button
          onClick={toggleAll}
          disabled={isProcessing}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      )}

      <div className="space-y-2">
        {pendingActions.map((action) => (
          <PendingActionItem
            key={action.id}
            action={action}
            isSelected={selectedIds.has(action.id)}
            onToggle={toggleAction}
            disabled={isProcessing}
          />
        ))}
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={handleConfirm}
          disabled={isProcessing || !someSelected}
          className={`
            flex-1 flex items-center justify-center gap-2 px-4 py-2
            text-sm font-medium rounded-lg transition-colors
            ${
              someSelected
                ? 'bg-green-700 text-white hover:bg-green-800'
                : 'bg-surface-sunken text-ink-3 cursor-not-allowed'
            }
            ${isProcessing ? 'opacity-75' : ''}
          `}
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              Approve {selectedIds.size > 0 && `(${selectedIds.size})`}
            </>
          )}
        </button>

        <button
          onClick={handleRejectAll}
          disabled={isProcessing}
          className="flex items-center justify-center gap-2 px-4 py-2
            text-sm font-medium rounded-lg transition-colors
            bg-surface-card border border-line-soft text-ink
            hover:bg-surface-sunken hover:border-ink-3/40"
        >
          <XCircle className="w-4 h-4" />
          Reject All
        </button>
      </div>
    </div>
  );
};

export default PendingActions;
